/**
 * The walkie-talkie send word — the spoken "over" that yields the floor.
 *
 * Matching is deliberately tail-only. A send word anywhere in the middle of a
 * sentence ("let's talk it over and decide") must not ship the turn; only a
 * trailing one counts, and trailing punctuation the transcriber invents
 * ("...over.") is ignored on both the match and the strip.
 *
 * The default is "over over" precisely because nobody says it by accident.
 * A single "over" is one syllable faster but fires on phrases like "think it
 * over", and a turn shipped mid-thought is not recoverable. Still editable,
 * since the right word depends on how you talk.
 */

const STORAGE_KEY = 'ctr.walkie.sendWord';

export const DEFAULT_SEND_WORD = 'over over';

/** Common alternatives offered in the UI; any text is allowed. */
export const SEND_WORD_PRESETS = ['over over', 'over', 'go ahead', 'send it'];

export function getSendWord() {
  try {
    const saved = (localStorage.getItem(STORAGE_KEY) || '').trim();
    return saved || DEFAULT_SEND_WORD;
  } catch {
    return DEFAULT_SEND_WORD;
  }
}

export function setSendWord(word) {
  const clean = (word || '').trim();
  try {
    if (clean) localStorage.setItem(STORAGE_KEY, clean);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode — the choice just won't persist.
  }
  return clean || DEFAULT_SEND_WORD;
}

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Lowercase, strip punctuation, collapse whitespace — compare like for like. */
const normalize = (t) =>
  (t || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Does `text` END with the send word? (The whole buffer counts as a match.) */
export function endsWithSendWord(text, word) {
  const n = normalize(text);
  const w = normalize(word);
  if (!w || !n) return false;
  return n === w || n.endsWith(` ${w}`);
}

/** Drop the trailing send word plus whatever punctuation came with it. */
export function stripSendWord(text, word) {
  const tokens = normalize(word).split(' ').filter(Boolean);
  if (!tokens.length) return (text || '').trim();
  const pattern = `[\\s,.!?]*${tokens.map(escapeRegex).join('[\\s,.!?]+')}[\\s,.!?]*$`;
  return (text || '').replace(new RegExp(pattern, 'i'), '').trim();
}
